import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { usePortal } from '../contexts/PortalContext';
import { ClientShell } from '../components/ClientShell';
import { Home } from './client/Home';
import { Project } from './client/Project';
import { Episodes } from './client/Episodes';
import { EpisodeReview } from './client/EpisodeReview';
import { Transcript } from './client/Transcript';
import { Files } from './client/Files';
import { Schedule } from './client/Schedule';

/** What a client link says while it loads, or when it cannot. */
const LOAD_MESSAGE: Record<string, {
  title: string;
  body: string;
}> = {
  missing: {
    title: 'Project unavailable',
    body: 'This project could not be found. Check the link Portal sent you, or get in touch and we will send it again.'
  },
  denied: {
    title: 'Access unavailable',
    body: 'This link is no longer valid. Get in touch with Portal and we will send you a new one.'
  },
  failed: {
    title: 'Project unavailable',
    body: 'This project could not be loaded. Reload to try again — if it keeps happening, let Portal know.'
  }
};
export function ClientApp() {
  const {
    clientRoute,
    clientLoad
  } = usePortal();

  /* The very first thing a client sees should never fade in from nothing —
     against the ink-filled "Project Status" card, a 0→1 opacity tween reads
     as a flash of the wrong color partway through rather than a considered
     transition. Only route changes AFTER that first paint get the fade;
     landing on the project for the first time paints immediately. */
  const hasPaintedOnce = useRef(false);
  const isFirstPaint = !hasPaintedOnce.current;
  useEffect(() => {
    hasPaintedOnce.current = true;
  });

  /* A client screen never renders on nothing: while the project is being read
     it says so, and if the read fails it says why. */
  if (clientLoad === 'loading') {
    return <ClientShell data-id="element-734" data-mp="qdyvzawsky5">
        <p className="label m-0 text-mute" data-id="element-735" data-mp="ot4qasib40t">Loading your project…</p>
      </ClientShell>;
  }
  const problem = LOAD_MESSAGE[clientLoad];
  if (problem) {
    return <ClientShell data-id="element-736" data-mp="3qf88u7o7z5uh">
        <p className="label m-0 text-mute" data-id="element-737" data-mp="3qeqdredamxo5">{problem.title}</p>
        <p className="mt-5 max-w-lg text-lg leading-relaxed" data-id="element-738" data-mp="3lfvnjrczy6u0">{problem.body}</p>
      </ClientShell>;
  }
  return <ClientShell data-id="element-739" data-mp="3ghc0fnmo2p02">
      <motion.div key={clientRoute} initial={isFirstPaint ? false : {
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-740" data-mp="3ghu5n9rb4dk4">
        {clientRoute === 'home' && <Home data-id="element-741" data-mp="d26icfiw8wn9" />}
        {clientRoute === 'project' && <Project data-id="element-742" data-mp="88lj6k5kdwmh" />}
        {clientRoute === 'episodes' && <Episodes data-id="element-743" data-mp="87aiwubbv704" />}
        {clientRoute === 'episode' && <EpisodeReview data-id="element-744" data-mp="398ya4y96zmj" />}
        {clientRoute === 'transcript' && <Transcript data-id="element-745" data-mp="10po4nvje32iu" />}
        {clientRoute === 'files' && <Files data-id="element-746" data-mp="10qg2ze4dfect" />}
        {clientRoute === 'schedule' && <Schedule data-id="element-747" data-mp="vyy02kisf3p7" />}
      </motion.div>
    </ClientShell>;
}